// Frontend brand service.
// Reads the brand profile injected server-side as window.__BRAND__.
// Falls back to the MednnectAI defaults when window.__BRAND__ is undefined
// (e.g. pure Vite dev server without the Node backend serving the HTML).

export interface BrandColors {
  accent: string;
  accentHover: string;
  accentLight: string;
  accentRgb: string;
  accentHoverRgb: string;
  accentLightRgb: string;

  bg: string;
  bgRgb: string;
  surface: string;
  surfaceRgb: string;
  surfaceHover: string;
  surfaceHoverRgb: string;
  surfaceMuted: string;
  surfaceInset: string;
  border: string;
  borderLight: string;
  borderRgb: string;
  text: string;
  textRgb: string;
  textSecondary: string;
  textSecondaryRgb: string;

  darkBg: string;
  darkBgRgb: string;
  darkSurface: string;
  darkSurfaceRgb: string;
  darkSurfaceHover: string;
  darkSurfaceHoverRgb: string;
  darkSurfaceMuted: string;
  darkSurfaceInset: string;
  darkBorder: string;
  darkBorderLight: string;
  darkBorderRgb: string;
  darkText: string;
  darkTextRgb: string;
  darkTextSecondary: string;
  darkTextSecondaryRgb: string;

  loginGradientFrom: string;
  loginGradientTo: string;
  loginButton: string;
  loginButtonHover: string;
  loginTitle: string;
  loginFocusRing: string;
  loginLinkHover: string;
}

export interface BrandProfileAdmin {
  email: string;
  password: string;
}

export interface BrandProfile {
  id: string;
  name: string;
  tagline?: string;
  colors: BrandColors;
  logoUrl: string;
  faviconUrl: string;
  dbName: string;
  admin: BrandProfileAdmin;
}

// Default profile — matches src/server/brand-profiles/mednnect.json.
// Used when window.__BRAND__ is not set (Vite dev mode without backend).
const DEFAULT_PROFILE: BrandProfile = {
  id: 'mednnect',
  name: 'MednnectAI',
  tagline: 'AI Agent',
  colors: {
    accent: '#3B82F6',
    accentHover: '#2563EB',
    accentLight: '#60A5FA',
    accentRgb: '59 130 246',
    accentHoverRgb: '37 99 235',
    accentLightRgb: '96 165 250',

    bg: '#F8F9FB',
    bgRgb: '248 249 251',
    surface: '#FFFFFF',
    surfaceRgb: '255 255 255',
    surfaceHover: '#F0F1F4',
    surfaceHoverRgb: '240 241 244',
    surfaceMuted: '#F3F4F6',
    surfaceInset: '#EBEDF0',
    border: '#E0E2E7',
    borderLight: '#EBEDF0',
    borderRgb: '224 226 231',
    text: '#1A1D23',
    textRgb: '26 29 35',
    textSecondary: '#6B7280',
    textSecondaryRgb: '107 114 128',

    darkBg: '#0F1117',
    darkBgRgb: '15 17 23',
    darkSurface: '#1A1D27',
    darkSurfaceRgb: '26 29 39',
    darkSurfaceHover: '#242830',
    darkSurfaceHoverRgb: '36 40 48',
    darkSurfaceMuted: '#151820',
    darkSurfaceInset: '#0C0E14',
    darkBorder: '#2A2E38',
    darkBorderLight: '#1F232B',
    darkBorderRgb: '42 46 56',
    darkText: '#E4E5E9',
    darkTextRgb: '228 229 233',
    darkTextSecondary: '#8B8FA3',
    darkTextSecondaryRgb: '139 143 163',

    loginGradientFrom: '#1a1a2e',
    loginGradientTo: '#16213e',
    loginButton: '#3B82F6',
    loginButtonHover: '#2563EB',
    loginTitle: '#1a1a2e',
    loginFocusRing: 'rgba(59, 130, 246, 0.15)',
    loginLinkHover: '#3B82F6',
  },
  logoUrl: '/branding/mednnect/logo.png',
  faviconUrl: '/branding/mednnect/logo.png',
  dbName: 'mednnectai.sqlite',
  admin: {
    email: 'admin@mednnect.ai',
    password: 'Mednnect@1234',
  },
};

declare global {
  interface Window {
    __BRAND__?: BrandProfile;
  }
}

class BrandService {
  private profile: BrandProfile;

  constructor() {
    this.profile =
      typeof window !== 'undefined' && window.__BRAND__
        ? window.__BRAND__
        : DEFAULT_PROFILE;
  }

  getProfile(): BrandProfile {
    return this.profile;
  }

  getName(): string {
    return this.profile.name;
  }

  getColors(): BrandColors {
    return this.profile.colors;
  }

  getLogoUrl(): string {
    return this.profile.logoUrl;
  }

  getFaviconUrl(): string {
    return this.profile.faviconUrl;
  }
}

export const brandService = new BrandService();